import axios from "axios";

/**
 * Production fallback host — used only in a real production runtime when the
 * CRA-injected env var is missing, empty, literal "undefined", or points at
 * a WebView-local host (capacitor:// or https://localhost). The AAB built by
 * GitHub Actions previously shipped without any REACT_APP_BACKEND_URL, which
 * meant `${undefined}/api` was baked in and every request went to
 * `https://localhost/undefined/api/...` in the Capacitor WebView. That
 * fallback protects the signed AAB and only the signed AAB.
 */
const PROD_API_HOST = "https://proximity.life";
const PROD_API_URL = `${PROD_API_HOST}/api`;

// process.env.NODE_ENV is one of: "production" | "development" | "test".
// CRA / craco set this at build/start time and never at runtime.
const NODE_ENV = process.env.NODE_ENV || "production";

/**
 * Recognise the two Capacitor Android WebView hosts. `https://localhost` is
 * the origin the WebView reports when androidScheme is "https"; `capacitor://`
 * is the fallback scheme. Both are WebView-internal and cannot serve /api.
 */
function isWebViewInternalHost(val) {
  return /^https?:\/\/localhost(?::\d+)?(\/|$)/i.test(val)
      || val.startsWith("capacitor://");
}

/**
 * Recognise developer-supplied localhost/loopback dev backends. Distinct from
 * `isWebViewInternalHost` because a WebView's own origin string looks the same
 * as a dev backend URL, but only during a production AAB run — never at
 * `yarn start` time. NODE_ENV separates the two cases cleanly.
 */
function isExplicitLocalDevBackend(val) {
  return /^https?:\/\/(?:localhost|127\.0\.0\.1|\[?::1\]?)(?::\d+)?(\/|$)/i.test(val);
}

function resolveApiBase() {
  const raw = process.env.REACT_APP_BACKEND_URL;
  const val = (raw == null ? "" : String(raw)).trim();

  // ---- Development / Test ----
  // Explicit intent is required. A missing or invalid dev config must FAIL
  // LOUDLY at module load time — never silently target production.
  if (NODE_ENV !== "production") {
    if (!val || val === "undefined") {
      const msg =
        "[proximity/api] REACT_APP_BACKEND_URL is missing or invalid in " +
        NODE_ENV + " mode. Set it explicitly (e.g. http://127.0.0.1:8011) " +
        "before starting the dev server. Refusing to fall back to production.";
      // Throwing here prevents axios from being constructed against
      // https://proximity.life during a local test run.
      throw new Error(msg);
    }
    // Explicit localhost / 127.0.0.1 / ::1 are HONOURED as-is in dev/test.
    // Any other https URL is also honoured (staging environments, etc.).
    return `${val.replace(/\/+$/, "")}/api`;
  }

  // ---- Production ----
  // At this point NODE_ENV === "production". The WebView-fallback rule
  // still stands: production must never resolve to a WebView-internal host.
  if (!val || val === "undefined" || isWebViewInternalHost(val)) {
    return PROD_API_URL;
  }
  return `${val.replace(/\/+$/, "")}/api`;
}

export const API = resolveApiBase();

/**
 * Domain used to build outward-facing shareable links (invites, join, accept,
 * Stripe return URLs, deep links). Must NEVER be `https://localhost` — that's
 * the origin Capacitor's Android WebView reports at runtime, and sharing a
 * `https://localhost/join/…` link would be broken for the recipient.
 *
 * Priority:
 *   1. REACT_APP_SHARE_DOMAIN (baked at build time — `https://proximity.life`
 *      for the production AAB via .env.production).
 *   2. window.location.origin — safe fallback for the browser preview and
 *      web-based PWA use, where the origin IS the real domain.
 *   3. Hard fallback to the production domain if we're on a non-web origin
 *      like `capacitor://` / `https://localhost` and no env override exists.
 */
export function shareOrigin() {
  const envDomain = process.env.REACT_APP_SHARE_DOMAIN;
  if (envDomain) return envDomain.replace(/\/+$/, "");
  if (typeof window === "undefined") return "https://proximity.life";
  const origin = window.location.origin || "";
  if (isWebViewInternalHost(origin)) {
    return "https://proximity.life";
  }
  return origin;
}

// ---------------------------------------------------------------------------
// Test-network guard
// ---------------------------------------------------------------------------
// A single, allow-listed HTTP destination is enforced for every outgoing
// request. If a request would ever target something other than the resolved
// `API` origin — because someone hard-codes a URL, a third-party lib leaks a
// hostname, or a bad env slipped through — the interceptor rejects the
// request BEFORE the network sees it.
//
// The check is defensive: it uses the same `API` origin that was resolved
// above, so anything that resolveApiBase() accepted (dev localhost, staging,
// or production) is honoured. Anything else is refused.
function assertAllowedApiTarget(url) {
  try {
    const target = new URL(url, API);
    const base   = new URL(API);
    if (target.origin === base.origin) return;
    // Same-origin relative paths (leading "/") also resolve to API's origin
    // via `new URL(..., API)`, so we should never reach here for those.
    const err = new Error(
      "[proximity/api] Blocked request to unexpected origin " +
      target.origin + " (expected " + base.origin + "). " +
      "This safeguard prevents accidental production traffic from a " +
      "misconfigured dev/test build."
    );
    err.code = "E_UNEXPECTED_API_ORIGIN";
    throw err;
  } catch (ex) {
    if (ex && ex.code === "E_UNEXPECTED_API_ORIGIN") throw ex;
    // If URL parsing failed the request is malformed — let axios raise.
  }
}
export { assertAllowedApiTarget as _assertAllowedApiTargetForTests };

// Auth strategy: Bearer token in localStorage (see interceptor below).
// We intentionally do NOT send cookies (`withCredentials: false`) because our
// ingress emits `Access-Control-Allow-Origin: *` which browsers/WebViews
// refuse to combine with `Access-Control-Allow-Credentials: true`. That
// combination silently breaks fetch on Capacitor Android. Bearer works
// everywhere: browsers, WebViews, native fetch, curl.
const client = axios.create({
  baseURL: API,
  withCredentials: false,
});

// Send Authorization header from localStorage (used by node devices where cookies may not persist)
client.interceptors.request.use((config) => {
  // Compute the exact URL axios will hit, then guard it. This runs before
  // any http adapter, so a blocked request never leaves the process.
  const url = axios.getUri({ ...config, baseURL: config.baseURL || API });
  assertAllowedApiTarget(url);

  const token = localStorage.getItem("session_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default client;
